import React, { useState, useMemo } from 'react';
import { X, ArrowRightLeft } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';

const money = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0.00';
  return Number(value).toFixed(2);
};

/**
 * Schedule a package change for a monthly afterschool enrollment.
 *
 * The discount is not editable here on purpose: the backend carries the
 * existing discount across as a ratio of the new package price, so the family
 * keeps the same proportional break. Change the discount from the enrollment
 * itself if it needs to move.
 */
const AfterschoolTransitionModal = ({
  enrollment, packages, apiCall, onClose, onSuccess, showError,
}) => {
  const [newPackageId, setNewPackageId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [billInAdvance, setBillInAdvance] = useState(false);
  const [saving, setSaving] = useState(false);

  const monthlyPackages = useMemo(
    () => packages.filter((p) => p.PackageID !== enrollment.PackageID && Number(p.MonthlyPrice) > 0),
    [packages, enrollment.PackageID]
  );

  // Mirrors the backend: new price, with the current discount re-applied at
  // the same ratio it holds against the current package.
  const preview = useMemo(() => {
    if (!newPackageId) return null;
    const pkg = packages.find((p) => p.PackageID === parseInt(newPackageId, 10));
    if (!pkg) return null;

    const newCost = Number(pkg.MonthlyPrice) || 0;
    let discount = 0;
    if (Number(enrollment.SiblingDiscount) > 0 && Number(enrollment.TotalCost) > 0) {
      discount = Math.round(newCost * (enrollment.SiblingDiscount / enrollment.TotalCost) * 100) / 100;
    }
    discount = Math.min(discount, newCost);
    const finalCost = Math.round((newCost - discount) * 100) / 100;

    return {
      packageName: pkg.PackageName,
      newCost,
      discount,
      finalCost,
      change: Math.round((finalCost - (Number(enrollment.FinalCost) || 0)) * 100) / 100,
    };
  }, [newPackageId, packages, enrollment]);

  const [confirming, setConfirming] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!preview) return;
    setConfirming(true);
  };

  const doSubmit = async () => {
    try {
      setSaving(true);
      await apiCall('/afterschool/transitions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enrollmentId: enrollment.EnrollmentID,
          newPackageId: parseInt(newPackageId, 10),
          effectiveDate,
          billInAdvance,
        }),
      });
      onSuccess();
    } catch (error) {
      showError('Error scheduling package change: ' + error.message);
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-blue-50 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <ArrowRightLeft className="w-6 h-6 text-purple-600" />
              Change Package
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              <strong>{enrollment.FirstName} {enrollment.LastName}</strong>
              {' · '}currently {enrollment.PackageName} at ${money(enrollment.FinalCost)}/month
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">New package *</label>
            <select
              value={newPackageId}
              onChange={(e) => setNewPackageId(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="">Select a package</option>
              {monthlyPackages.map((p) => (
                <option key={p.PackageID} value={p.PackageID}>
                  {p.PackageName} {p.TimeSlot ? `(${p.TimeSlot})` : ''} — ${money(p.MonthlyPrice)}/month
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Effective date *</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Usually the 1st of the month.</p>
          </div>

          {preview && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-2 text-sm">
              <h3 className="font-bold text-gray-800 mb-2">New pricing</h3>
              <div className="flex justify-between">
                <span className="text-gray-600">Current monthly cost</span>
                <span className="font-semibold">${money(enrollment.FinalCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">New package price</span>
                <span className="font-semibold">${money(preview.newCost)}</span>
              </div>
              {preview.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount carried over</span>
                  <span className="font-semibold">-${money(preview.discount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <span className="font-bold text-gray-800">New monthly cost</span>
                <span className="font-bold text-blue-600 text-lg">${money(preview.finalCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Change</span>
                <span className={`font-semibold ${preview.change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {preview.change > 0 ? '+' : ''}${money(preview.change)}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <input
              type="checkbox"
              id="billInAdvance"
              checked={billInAdvance}
              onChange={(e) => setBillInAdvance(e.target.checked)}
              className="w-5 h-5 text-blue-600 mt-0.5"
            />
            <label htmlFor="billInAdvance" className="text-sm text-gray-700 cursor-pointer">
              <strong>Bill in advance</strong> — charge the family for the new package now
              {preview && billInAdvance && (
                <span className="block text-purple-700 font-semibold mt-1">
                  ${money(preview.finalCost)} will be added to the balance immediately.
                </span>
              )}
            </label>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !newPackageId || !effectiveDate}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-semibold"
            >
              {saving ? 'Scheduling...' : 'Schedule Change'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={confirming}
        tone="question"
        title="Schedule package change"
        message={preview
          ? `Move ${enrollment.FirstName} ${enrollment.LastName} to ${preview.packageName} on ${
              effectiveDate ? new Date(effectiveDate).toLocaleDateString() : ''}?`
          : ''}
        details={preview
          ? `New rate $${money(preview.finalCost)}/month (${preview.change >= 0 ? '+' : ''}$${money(preview.change)} vs today).`
            + (billInAdvance ? ` The family will be charged $${money(preview.finalCost)} immediately.` : '')
          : ''}
        confirmLabel="Schedule change"
        busy={saving}
        onConfirm={doSubmit}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
};

export default AfterschoolTransitionModal;
